import { createContext, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { ColumnDef } from '@tanstack/react-table'
import type { RecoveryRunbookTask } from '@/types/entities'
import { truncateToMinuteFloor } from '@/lib/dateUtils'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { findMode, findMethod } from '@/features/objects/utils/runbookAutomation'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

/** Parse dependency numbers from comma-separated string */
function parseDependencyNumbers(deps: string | null | undefined): number[] {
    if (!deps || !deps.trim()) return []
    return deps
        .split(',')
        .map((d) => d.trim())
        .filter((d) => d !== '')
        .map((d) => parseInt(d, 10))
        .filter((d) => !isNaN(d))
}

/**
 * Compute forecast start and finish times for all tasks in dependency order.
 * Chains forecast times: task N's forecast start = max(forecast finish of dependencies, planned start, NOW).
 * This ensures pending tasks get sequential forecast times instead of all clamping to NOW.
 */
export function computeForecastTimesMap(
    allTasks: (RecoveryRunbookTask & { _index?: number })[],
    forecastBaseMs: number
): Map<number, { forecastStartMs: number; forecastFinishMs: number }> {
    const nowMs = truncateToMinuteFloor(new Date()).getTime()
    const map = new Map<number, { forecastStartMs: number; forecastFinishMs: number }>()

    // Sort by taskOffset (ascending) so dependencies are processed before dependents
    const sorted = [...allTasks].sort((a, b) => {
        const offsetA = a.taskOffset ?? a.forecastTaskOffset ?? 9999
        const offsetB = b.taskOffset ?? b.forecastTaskOffset ?? 9999
        return offsetA - offsetB
    })

    for (const task of sorted) {
        const taskNo = task.taskNo != null ? Number(task.taskNo) : null
        if (taskNo == null || isNaN(taskNo)) continue

        const status = (task.taskStatus || 'Pending').toLowerCase()
        const actualStart = task.actualStart
        const actualFinish = task.actualFinish
        // For pending tasks with no actuals, always use planned duration —
        // forecastDuration may be stale from a previous run
        const duration = (status === 'pending' && !actualStart && !actualFinish)
            ? (task.duration ?? 0)
            : (task.forecastDuration ?? task.duration ?? 0)
        const offset = task.forecastTaskOffset ?? task.taskOffset ?? 0
        const depNos = parseDependencyNumbers(task.dependencies)

        let forecastStartMs: number
        if (actualStart) {
            forecastStartMs = new Date(actualStart).getTime()
        } else {
            // Max of: when dependencies finish, planned start (base + offset), and NOW
            let depLatestFinishMs = 0
            for (const depNo of depNos) {
                const depForecast = map.get(depNo)
                if (depForecast && depForecast.forecastFinishMs > depLatestFinishMs) {
                    depLatestFinishMs = depForecast.forecastFinishMs
                }
            }
            const plannedStartMs = forecastBaseMs + offset * 60 * 1000
            forecastStartMs = Math.max(depLatestFinishMs, plannedStartMs, nowMs)
        }

        let forecastFinishMs: number
        if (actualFinish) {
            forecastFinishMs = new Date(actualFinish).getTime()
        } else {
            forecastFinishMs = forecastStartMs + duration * 60 * 1000
        }

        map.set(taskNo, { forecastStartMs, forecastFinishMs })
    }

    return map
}

export const DropdownOpenRowIndexContext = createContext<number | null>(null)
export const ExpandedRowsContext = createContext<Set<number>>(new Set())
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Copy } from 'lucide-react'
import { useState, useRef, useEffect, memo } from 'react'
import { useToast } from '@/hooks/use-toast'
import { useUsers } from '@/features/users/hooks'
import { TranslatableText } from '@/components/ui/TranslatableText'

// 'Skipped' is the branch nobody is running. It is a status rather than a deletion so the
// fallback stays visible and switchable — the whole point of holding both paths.
const TASK_STATUS_OPTIONS = ['Pending', 'Started', 'Completed', 'Issue', 'Skipped'] as const

const TASK_STATUS_I18N: Record<string, string> = {
    Pending: 'recoverRequests.taskStatusWaiting',
    Started: 'recoverRequests.taskStatusInProgress',
    Completed: 'recoverRequests.taskStatusComplete',
    Issue: 'recoverRequests.taskStatusIssue',
    Skipped: 'recoverRequests.taskStatusSkipped',
}

// Read-only text cell with copy
const ReadOnlyTextCell = memo(function ReadOnlyTextCell({
    value,
    placeholder,
    showCopyIcon = false,
    className,
    lineClamp = false,
    translatable = true,
}: {
    value: string
    placeholder: string
    showCopyIcon?: boolean
    className?: string
    lineClamp?: boolean
    translatable?: boolean
}) {
    const { toast } = useToast()

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (value) {
            try {
                await navigator.clipboard.writeText(value)
                toast({
                    title: 'Copied',
                    description: 'Text copied to clipboard',
                })
            } catch (err) {
                toast({
                    title: 'Error',
                    description: 'Failed to copy text',
                    variant: 'destructive',
                })
            }
        }
    }

    const clampClass = lineClamp ? 'line-clamp-2' : ''

    return (
        <div className="w-full flex items-start gap-1" onMouseDown={(e) => e.stopPropagation()}>
            <div className={`min-h-8 flex-1 rounded-md px-1.5 py-1 text-xs flex items-center ${className || ''}`} style={{ minWidth: 0 }}>
                {value ? (
                    <span className={`text-xs w-full whitespace-pre-wrap break-words ${clampClass} ${className || ''}`}>
                        {translatable ? <TranslatableText text={value} /> : value}
                    </span>
                ) : (
                    <span className={`text-xs w-full whitespace-pre-wrap break-words text-muted-foreground ${clampClass} ${className || ''}`}>{placeholder}</span>
                )}
            </div>
            {showCopyIcon && value && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 mt-1 flex-shrink-0"
                    onClick={handleCopy}
                    title="Copy to clipboard"
                >
                    <Copy className="h-3.5 w-3.5" />
                </Button>
            )}
        </div>
    )
})

// Editable status select - exported for RunbookRecoveryTable Card Timeline
export const EditableStatusCell = memo(function EditableStatusCell({
    value,
    onUpdate,
    onDropdownOpenChange,
}: {
    value: string | null
    onUpdate: (value: string) => void
    onDropdownOpenChange?: (open: boolean) => void
}) {
    const { t } = useTranslation()
    const [localValue, setLocalValue] = useState(value || 'Pending')

    useEffect(() => {
        setLocalValue(value || 'Pending')
    }, [value])

    const statusBgClass =
        localValue === 'Started'
            ? 'bg-yellow-200'
            : localValue === 'Completed'
              ? 'bg-green-200'
              : localValue === 'Issue'
                ? 'bg-red-200'
                : localValue === 'Skipped'
                    ? 'bg-gray-200 text-gray-600 line-through'
                    : ''

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <Select
                value={localValue}
                onValueChange={(v) => {
                    setLocalValue(v)
                    onUpdate(v)
                }}
                onOpenChange={onDropdownOpenChange}
            >
                <SelectTrigger className={`h-8 w-24 min-w-24 px-1.5 text-xs justify-center focus:ring-0 focus:ring-offset-0 ${statusBgClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <SelectValue>{TASK_STATUS_I18N[localValue] ? t(TASK_STATUS_I18N[localValue]) : localValue}</SelectValue>
            </SelectTrigger>
            <SelectContent className="text-xs">
                {TASK_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                        {TASK_STATUS_I18N[status] ? t(TASK_STATUS_I18N[status]) : status}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
        </div>
    )
})

// Read-only owner display (resolves user id to name)
export const ReadOnlyOwnerDisplay = memo(function ReadOnlyOwnerDisplay({ value }: { value: string | null }) {
    const { data: usersResponse } = useUsers({ perPage: 500 })
    const users = usersResponse?.data || []
    if (!value) return <span className="text-xs text-muted-foreground">—</span>
    const idNum = parseInt(value, 10)
    if (!isNaN(idNum)) {
        const user = users.find((u) => u.id === idNum)
        return <span className="text-xs">{user?.name ?? value}</span>
    }
    return <span className="text-xs">{value}</span>
})

// Editable user/owner select - names from users table (exported for RunbookRecoveryTable)
export const EditableOwnerCell = memo(function EditableOwnerCell({
    value,
    onUpdate,
    onDropdownOpenChange,
}: {
    value: string | null
    onUpdate: (value: string) => void
    onDropdownOpenChange?: (open: boolean) => void
}) {
    const { data: usersResponse } = useUsers({ perPage: 500 })
    const users = usersResponse?.data || []
    const [localValue, setLocalValue] = useState(value || '')

    useEffect(() => {
        setLocalValue(value || '')
    }, [value])

    const displayName = (() => {
        if (!localValue) return null
        const idNum = parseInt(localValue, 10)
        if (!isNaN(idNum)) {
            const user = users.find((u) => u.id === idNum)
            return user?.name || localValue
        }
        return localValue
    })()

    const selectValue = localValue || '__none__'
    const isLegacyValue = localValue && !users.some((u) => String(u.id) === localValue)

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <Select
                value={selectValue}
                onValueChange={(v) => {
                    const newVal = v === '__none__' ? '' : v
                    setLocalValue(newVal)
                    onUpdate(newVal)
                }}
                onOpenChange={onDropdownOpenChange}
            >
                <SelectTrigger className="h-8 w-24 min-w-24 px-1.5 text-xs truncate justify-center focus:ring-0 focus:ring-offset-0" onMouseDown={(e) => e.stopPropagation()}>
                <SelectValue placeholder="-">
                    {displayName || '-'}
                </SelectValue>
            </SelectTrigger>
            <SelectContent className="text-xs">
                <SelectItem value="__none__">-</SelectItem>
                {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                        {user.name}
                    </SelectItem>
                ))}
                {isLegacyValue && (
                    <SelectItem value={localValue}>{displayName || localValue}</SelectItem>
                )}
            </SelectContent>
        </Select>
        </div>
    )
})

export function createRunbookRecoveryColumns(
    onTaskChange: (index: number, field: keyof RecoveryRunbookTask, value: any) => void,
    t: (key: string) => string,
    locale: string | undefined,
    _forecastStartTime: Date | null, // Unused; forecast times calculated from forecastTaskOffset like runbook master/object
    allTasks: (RecoveryRunbookTask & { _index: number })[],
    showFullTasks: boolean,
    onDropdownOpenChange?: (open: boolean, rowIndex?: number) => void,
    readOnly?: boolean
): ColumnDef<RecoveryRunbookTask & { _index: number }>[] {
    const effectiveLocale = locale || 'en-US'
    // Forecast base: task 1 start = NOW() unless there is actual start for task 1 (like runbook master/object)
    const task1 = allTasks.length > 0
        ? (allTasks.find(t => Number(t.taskNo) === 1) ?? allTasks.reduce((a, b) => ((a.taskOffset ?? 9999) < (b.taskOffset ?? 9999) ? a : b), allTasks[0]))
        : null
    const forecastBaseMs = task1?.actualStart
        ? truncateToMinuteFloor(new Date(task1.actualStart)).getTime()
        : truncateToMinuteFloor(new Date()).getTime()

    const forecastTimesMap = computeForecastTimesMap(allTasks, forecastBaseMs)

    return [
        {
            accessorKey: 'waitGo',
            header: t('objects.runbook.waitGo') || 'WaitGo',
            size: 44,
            minSize: 40,
            maxSize: 52,
            cell: ({ row }) => {
                const waitGo = (row.getValue('waitGo') as string | null) || 'WAIT'
                const waitGoUpper = waitGo.toUpperCase()
                const isGo = waitGoUpper === 'GO'
                return (
                    <div className="flex justify-center" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <div
                            className={`min-w-9 w-9 min-h-6 h-6 rounded flex items-center justify-center text-xs font-medium text-white shrink-0 ${
                                isGo ? 'bg-[rgb(151,200,100)]' : 'bg-[rgb(232,96,55)]'
                            }`}
                        >
                            {waitGoUpper}
                        </div>
                    </div>
                )
            },
        },
        {
            accessorKey: 'mode',
            header: t('objects.runbook.automation') || 'Automation',
            // Icons only — during a live recovery the horizontal space belongs to the task
            // text, and the words are one hover away.
            size: 64,
            minSize: 56,
            maxSize: 76,
            cell: ({ row }) => {
                // Recovery runbooks are read-only: the tag is inherited from the object
                // runbook. "Armed" = dependencies met (GO) and not yet finished, i.e.
                // ready for the engineer to review and fire.
                const mode = ((row.getValue('mode') as string | null) || 'manual').toLowerCase()
                const modeOption = findMode(mode)
                const ModeIcon = modeOption.icon
                const armedable = mode === 'assisted' || mode === 'agent'
                const waitGo = ((row.original.waitGo as string | null) || 'WAIT').toUpperCase()
                const status = ((row.original.taskStatus as string | null) || 'Pending').toLowerCase()
                const armed = armedable && waitGo === 'GO' && status !== 'completed'
                const method = ((row.original.method as string | null) || '').toLowerCase()
                const showMethod = armedable && method && method !== 'manual'
                const MethodIcon = showMethod ? findMethod(method).icon : null

                // The tooltip carries what the icons cannot: the words, the handler, and
                // whether this task is actually ready to fire right now.
                const tip = [
                    t(modeOption.labelKey),
                    showMethod ? t(findMethod(method).labelKey) : null,
                    armedable
                        ? (armed
                            ? (t('objects.runbook.armedReady', 'Armed — ready to run'))
                            : (t('objects.runbook.armedWaiting', 'Waiting for dependencies')))
                        : null,
                ].filter(Boolean).join(' · ')

                return (
                    <div
                        className="flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <TooltipProvider delayDuration={150}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span
                                        className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 ${
                                            armed
                                                ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-300 dark:bg-purple-950/40 dark:text-purple-300'
                                                : armedable
                                                    ? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                                                    : 'text-muted-foreground'
                                        }`}
                                        aria-label={tip}
                                    >
                                        <ModeIcon className="h-4 w-4" aria-hidden />
                                        {MethodIcon ? <MethodIcon className="h-4 w-4 opacity-70" aria-hidden /> : null}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>{tip}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                )
            },
        },
        {
            accessorKey: 'taskNo',
            header: t('objects.runbook.taskNo'),
            size: 44,
            minSize: 40,
            maxSize: 52,
            cell: ({ row }) => (
                <div className="flex justify-center" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                    <span className="text-xs font-bold text-black w-fit px-1.5">{row.getValue('taskNo') ?? '-'}</span>
                </div>
            ),
        },
        {
            accessorKey: 'task',
            header: t('objects.runbook.task'),
            size: 320,
            minSize: 260,
            maxSize: 450,
            cell: ({ row }) => {
                const index = row.original._index
                const dropdownOpenRowIndex = useContext(DropdownOpenRowIndexContext)
                const expandedRows = useContext(ExpandedRowsContext)
                const isExpanded = showFullTasks || dropdownOpenRowIndex === index || expandedRows.has(index)
                return (
                    <ReadOnlyTextCell
                        value={(row.getValue('task') as string) || ''}
                        placeholder={t('objects.runbook.task')}
                        showCopyIcon
                        lineClamp={!isExpanded}
                    />
                )
            },
        },
        {
            accessorKey: 'commands',
            header: t('objects.runbook.commands'),
            size: 200,
            minSize: 150,
            maxSize: 280,
            cell: ({ row }) => (
                <ReadOnlyTextCell value={(row.getValue('commands') as string) || ''} placeholder={t('objects.runbook.commands')} showCopyIcon className="text-muted-foreground" translatable={false} />
            ),
        },
        {
            accessorKey: 'duration',
            header: t('objects.runbook.duration'),
            size: 52,
            minSize: 44,
            maxSize: 64,
            cell: ({ row }) => {
                const duration = row.getValue('duration') as number | null
                const forecastDuration = row.getValue('forecastDuration') as number | null
                const taskStatus = row.getValue('taskStatus') as string | null
                const actualStart = row.getValue('actualStart') as string | null
                const isStarted = String(taskStatus || '').toUpperCase() === 'STARTED'
                const showProgress = isStarted && actualStart && duration != null && duration > 0
                const isOverrun = forecastDuration != null && duration != null && forecastDuration > duration
                let percent = 0
                if (showProgress) {
                    const startMs = new Date(actualStart).getTime()
                    const elapsedMins = (Date.now() - startMs) / 60000
                    percent = Math.min(100, Math.max(0, (elapsedMins / duration) * 100))
                }
                const barColorClass = isOverrun ? 'bg-red-500' : 'bg-primary'
                return (
                    <div className="flex flex-col gap-0.5 w-full min-w-0 max-w-full overflow-hidden" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <div className="h-8 min-w-[2rem] px-1.5 rounded-md text-xs flex items-center justify-center shrink-0">
                            {duration != null ? formatSecondsToDHM(duration * 60) : '-'}
                        </div>
                        {showProgress && (
                            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden" title={`${Math.round(percent)}% elapsed`}>
                                <div
                                    className={`h-full rounded-full ${barColorClass} transition-all duration-300`}
                                    style={{ width: `${percent}%` }}
                                />
                            </div>
                        )}
                    </div>
                )
            },
        },
        {
            accessorKey: 'plannedStart',
            header: t('objects.runbook.startTime'),
            size: 120,
            minSize: 100,
            maxSize: 140,
            cell: ({ row }) => {
                const val = row.getValue('plannedStart') as string | null
                if (!val) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                try {
                    const d = new Date(val)
                    const dateStr = d.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                    const timeStr = d.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                    return <div className="flex justify-center"><span className="text-xs text-muted-foreground whitespace-nowrap w-fit px-1.5">{dateStr} {timeStr}</span></div>
                } catch {
                    return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                }
            },
        },
        {
            accessorKey: 'plannedFinish',
            header: t('objects.runbook.finishTime'),
            size: 120,
            minSize: 100,
            maxSize: 140,
            cell: ({ row }) => {
                const val = row.getValue('plannedFinish') as string | null
                if (!val) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                try {
                    const d = new Date(val)
                    const dateStr = d.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                    const timeStr = d.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                    return <div className="flex justify-center"><span className="text-xs text-muted-foreground whitespace-nowrap w-fit px-1.5">{dateStr} {timeStr}</span></div>
                } catch {
                    return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                }
            },
        },
        {
            accessorKey: 'taskOffset',
            header: t('objects.runbook.taskOffset') || 'Task Offset',
            size: 64,
            minSize: 52,
            maxSize: 80,
            enableHiding: true,
            cell: ({ row }) => (
                <div className="flex justify-center">
                    <span className="text-xs text-muted-foreground w-fit px-1.5">
                        {(row.getValue('taskOffset') as number | null) != null ? `${row.getValue('taskOffset')}${t('objects.runbook.minutesShort') || 'm'}` : '-'}
                    </span>
                </div>
            ),
        },
        {
            accessorKey: 'taskStatus',
            header: t('recoverRequests.taskStatus') || 'Status',
            size: 100,
            minSize: 88,
            maxSize: 120,
            cell: ({ row }) => {
                const index = row.original._index
                const value = (row.getValue('taskStatus') as string | null) || null
                if (readOnly) {
                    return (
                        <div className="flex justify-center">
                            <span className="text-xs px-2 py-0.5">{value ?? '—'}</span>
                        </div>
                    )
                }
                return (
                    <div className="flex justify-center">
                        <EditableStatusCell
                            value={value}
                            onUpdate={(v) => onTaskChange(index, 'taskStatus', v)}
                            onDropdownOpenChange={(open) => onDropdownOpenChange?.(open, index)}
                        />
                    </div>
                )
            },
        },
        {
            accessorKey: 'taskOwner',
            header: t('recoverRequests.taskOwner') || 'User',
            size: 100,
            minSize: 80,
            maxSize: 120,
            cell: ({ row }) => {
                const index = row.original._index
                const value = (row.getValue('taskOwner') as string | null) || null
                if (readOnly) {
                    return (
                        <div className="flex justify-center">
                            <ReadOnlyOwnerDisplay value={value} />
                        </div>
                    )
                }
                return (
                    <div className="flex justify-center">
                        <EditableOwnerCell
                            value={value}
                            onUpdate={(v) => onTaskChange(index, 'taskOwner', v)}
                            onDropdownOpenChange={(open) => onDropdownOpenChange?.(open, index)}
                        />
                    </div>
                )
            },
        },
        {
            accessorKey: 'dependencies',
            header: t('objects.runbook.dependencies'),
            size: 100,
            minSize: 80,
            maxSize: 120,
            cell: ({ row }) => {
                const deps = row.getValue('dependencies') as string
                const taskNo = row.original.taskNo
                if (taskNo === 1) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">{t('objects.runbook.noDependencies')}</span></div>
                return <div className="flex justify-center"><span className="text-xs w-fit px-1.5">{deps || '-'}</span></div>
            },
        },
        {
            accessorKey: 'actualStart',
            header: t('objects.runbook.actualStart') || 'Actual Start',
            size: 120,
            minSize: 100,
            maxSize: 140,
            cell: ({ row }) => {
                        const val = row.getValue('actualStart') as string | null
                        if (!val) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                        try {
                            const d = new Date(val)
                            const dateStr = d.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                            const timeStr = d.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                            return <div className="flex justify-center"><span className="text-xs text-muted-foreground whitespace-nowrap w-fit px-1.5">{dateStr} {timeStr}</span></div>
                        } catch {
                            return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                        }
                    },
                },
        {
            accessorKey: 'actualFinish',
            header: t('objects.runbook.actualFinish') || 'Actual Finish',
            size: 120,
            minSize: 100,
            maxSize: 140,
            cell: ({ row }) => {
                const val = row.getValue('actualFinish') as string | null
                if (!val) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                try {
                    const d = new Date(val)
                    const dateStr = d.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                    const timeStr = d.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                    return <div className="flex justify-center"><span className="text-xs text-muted-foreground whitespace-nowrap w-fit px-1.5">{dateStr} {timeStr}</span></div>
                } catch {
                    return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                }
            },
        },
        {
            accessorKey: 'actualDuration',
            header: t('objects.runbook.actualDuration') || 'Actual Duration',
                    size: 64,
                    minSize: 52,
                    maxSize: 80,
                    cell: ({ row }) => {
                        const val = row.getValue('actualDuration') as number | null
                        if (val == null) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                        return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">{val}{t('objects.runbook.minutesShort') || 'm'}</span></div>
                    },
        },
        {
            accessorKey: 'forecastStart',
            header: t('objects.runbook.forecastStart') || 'Forecast Start',
            size: 120,
            minSize: 100,
            maxSize: 140,
            cell: ({ row }) => {
                const taskNo = row.original.taskNo != null ? Number(row.original.taskNo) : null
                if (taskNo == null || isNaN(taskNo)) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                const forecast = forecastTimesMap.get(taskNo)
                if (!forecast) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                const d = new Date(forecast.forecastStartMs)
                const dateStr = d.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                const timeStr = d.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                return <div className="flex justify-center"><span className="text-xs text-muted-foreground whitespace-nowrap w-fit px-1.5">{dateStr} {timeStr}</span></div>
            },
        },
        {
            accessorKey: 'forecastFinish',
            header: t('objects.runbook.forecastFinish') || 'Forecast Finish',
            size: 120,
            minSize: 100,
            maxSize: 140,
            cell: ({ row }) => {
                const taskNo = row.original.taskNo != null ? Number(row.original.taskNo) : null
                if (taskNo == null || isNaN(taskNo)) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                const forecast = forecastTimesMap.get(taskNo)
                if (!forecast) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                const d = new Date(forecast.forecastFinishMs)
                const dateStr = d.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                const timeStr = d.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                return <div className="flex justify-center"><span className="text-xs text-muted-foreground whitespace-nowrap w-fit px-1.5">{dateStr} {timeStr}</span></div>
            },
        },
        {
            accessorKey: 'forecastDuration',
            header: t('objects.runbook.forecastDuration') || 'Forecast Duration',
            size: 64,
            minSize: 52,
            maxSize: 80,
            cell: ({ row }) => {
                const taskNo = row.original.taskNo != null ? Number(row.original.taskNo) : null
                if (taskNo == null || isNaN(taskNo)) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                const forecast = forecastTimesMap.get(taskNo)
                if (!forecast) return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">-</span></div>
                const durationMs = forecast.forecastFinishMs - forecast.forecastStartMs
                const durationMin = Math.round(durationMs / 60000)
                return <div className="flex justify-center"><span className="text-xs text-muted-foreground w-fit px-1.5">{durationMin}{t('objects.runbook.minutesShort') || 'm'}</span></div>
            },
        },
        {
            accessorKey: 'forecastTaskOffset',
            header: t('objects.runbook.forecastTaskOffset') || 'Forecast Task Offset',
            size: 64,
            minSize: 52,
            maxSize: 80,
            enableHiding: true,
            cell: ({ row }) => (
                <div className="flex justify-center">
                    <span className="text-xs text-muted-foreground w-fit px-1.5">
                        {(row.getValue('forecastTaskOffset') as number | null) != null ? `${row.getValue('forecastTaskOffset')}${t('objects.runbook.minutesShort') || 'm'}` : '-'}
                    </span>
                </div>
            ),
        },
    ]
}
